import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { VaLink } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { focusElement } from 'platform/utilities/ui';
import { REPEAT_INELIGIBILITY_ANCHOR_ID } from './ChampvaEligibilityCards';

/**
 * Yellow warning alert shown in Step 2 when at least one not-enrolled applicant
 * has received a second (or later) VES-sent CCL letter — i.e. when the BE sets
 * repeatIneligibilityAlert on the claim response root.
 *
 * Replaces ChampvaClosedAlert when repeatIneligibilityAlert is present.
 * Only rendered inside the isChampvaClaim gate in ClaimStatusPage.
 *
 * The link label comes from claimStatusMeta.repeatIneligibilityCard.linkText so
 * content can change it without a FE deploy, with a FE default so the link never
 * renders unlabeled when that config is missing. Its target is set by
 * ChampvaEligibilityCards on the first applicant card with repeat letter activity.
 */
export const DEFAULT_LINK_TEXT = 'Read updated decision';

export default function ChampvaRepeatIneligibilityAlert({ alert, linkText }) {
  const linkRef = useRef(null);

  // Anchor href alone does not reliably move screen reader focus, so focus is
  // moved explicitly. focusElement applies (and later cleans up) the tabindex
  // the heading needs to receive programmatic focus, and scrolls it into view.
  const handleLinkClick = useCallback(event => {
    const target = document.getElementById(REPEAT_INELIGIBILITY_ANCHOR_ID);
    if (!target) return;

    event.preventDefault();
    focusElement(target);
  }, []);

  // This is the only click handler for the link — deliberately not a React
  // onClick on VaLink. va-link renders its <a> inside a shadow root, so the
  // binding's onClick never reaches the host in a browser, and a listener on the
  // host fires too late to stop the anchor's navigation. That hash change
  // remounts ClaimStatusPage, whose setPageFocus then moves focus to .tab-header,
  // undoing the focus this link just set. Intercepting on document in the capture
  // phase runs before the anchor is reached, so preventDefault reliably cancels
  // the navigation. composedPath() sees through the shadow boundary to identify
  // our link; propagation is left alone so va-link's analytics still fire.
  useEffect(() => {
    // Nothing renders without an alert, so skip attaching a document-wide
    // listener on the far more common non-repeat path.
    if (!alert) return undefined;

    const onDocumentClick = event => {
      const host = linkRef.current;
      if (!host) return;

      const path = event.composedPath ? event.composedPath() : [];
      if (!path.includes(host)) return;

      handleLinkClick(event);
    };

    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, [alert, handleLinkClick]);

  if (!alert) return null;

  const { title, description } = alert;

  return (
    <va-alert
      data-testid="champva-repeat-ineligibility-alert"
      class="vads-u-margin-bottom--4"
      status="warning"
    >
      <h2 id="claims-alert-header" slot="headline">
        {title}
      </h2>
      {description && <p className="vads-u-margin-y--0">{description}</p>}
      <p className="vads-u-margin-bottom--0">
        <VaLink
          ref={linkRef}
          data-testid="champva-repeat-ineligibility-link"
          href={`#${REPEAT_INELIGIBILITY_ANCHOR_ID}`}
          text={linkText || DEFAULT_LINK_TEXT}
        />
      </p>
    </va-alert>
  );
}

ChampvaRepeatIneligibilityAlert.propTypes = {
  alert: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
  }),
  linkText: PropTypes.string,
};
